/**
 * OTP Service - Communicates with self-hosted OTP backend
 * Zero Twilio/AWS dependency
 */

const API_BASE_URL = process.env.REACT_APP_OTP_API_URL || 'http://localhost:5000/api/otp';

export interface OTPRequest {
  identifier: string; // email or phone number
  method: 'email' | 'sms';
}

export interface OTPVerify {
  identifier: string;
  otp: string;
}

export interface OTPResponse {
  success: boolean;
  message?: string;
  error?: string;
  otp?: string; // Only in development mode
}

class OTPService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Request OTP via email or SMS
   */
  async requestOTP(identifier: string, method: 'email' | 'sms' = 'email'): Promise<OTPResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier, method }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request OTP');
      }

      return data;
    } catch (error: any) {
      console.error('Error requesting OTP:', error);
      return {
        success: false,
        error: error.message || 'Failed to request OTP. Please try again.',
      };
    }
  }

  /**
   * Verify OTP
   */
  async verifyOTP(identifier: string, otp: string): Promise<OTPResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify OTP');
      }

      return data;
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      return {
        success: false,
        error: error.message || 'Failed to verify OTP. Please try again.',
      };
    }
  }

  /**
   * Resend OTP
   */
  async resendOTP(identifier: string, method: 'email' | 'sms' = 'email'): Promise<OTPResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier, method }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend OTP');
      }

      return data;
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      return {
        success: false,
        error: error.message || 'Failed to resend OTP. Please try again.',
      };
    }
  }

  /**
   * Check if OTP service is available
   */
  async healthCheck(): Promise<boolean> {
    try {
      const baseUrl = this.baseUrl.replace('/api/otp', '');
      const response = await fetch(`${baseUrl}/health`);
      return response.ok;
    } catch (error) {
      console.error('OTP service health check failed:', error);
      return false;
    }
  }
}

export const otpService = new OTPService();
export default otpService;

